import { inject, Injectable } from '@angular/core';
import { AlertType } from '@gains-com/ui-angular';
import { ApiService, ErrorMapType } from './api.service.js';
import {
  DocumentVerificationViewService,
  DocumentVerificationDTO,
} from './view-states/document-verification-view.service.js';
import {
  DocumentVerificationTypeId,
  TargetEntityQuery,
} from '@gains-com/shared';

export { DocumentVerificationDTO };

export interface CreateDocumentVerificationPersonaResponse {
  persona?: {
    inquiry_id: string;
    resume_token: string;
  } | null;
}

export interface DocumentVerificationCheckDTO {
  document_verification_check_id: string;
  rule_type_id: string;
  rule_label: string;
  pass: boolean | null;
  points_earned: number;
  evaluated_at: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class DocumentVerificationService extends ApiService {
  readonly ErrorMap: ErrorMapType = {
    INTERNAL_SERVER_ERROR: {
      swallowError: false,
      title: 'Unknown Error',
      message:
        'An unknown problem occurred. Please contact customer support if the problem persists.',
      type: AlertType.Danger,
    },
    VERIFICATION_NOT_FOUND: {
      type: AlertType.Danger,
      swallowError: false,
      title: 'Verification Not Found',
      message: 'The verification could not be found.',
    },
    VERIFICATION_ACCESS_DENIED: {
      type: AlertType.Danger,
      swallowError: false,
      title: 'Access Denied',
      message: 'You do not have access to this verification.',
    },
    VERIFICATION_ALREADY_FINALIZED: {
      type: AlertType.Warning,
      swallowError: true,
      title: 'Verification Complete',
      message: 'This verification has already been finalized.',
    },
  };

  private readonly _viewService = inject(DocumentVerificationViewService);

  /**
   * Starts document verification using the Persona document verification strategy
   *
   * If a target entity is provided, the document entity created as a result of the
   * inquiry will be immediately linked to the target entity's document verification
   * (e.g., the bank statement will be linked to the verification of the payment method)
   *
   * If a target entity query is provided instead (mutually exclusive with targetEntity),
   * the backend will search for a matching verification and link the document to it.
   */
  async createDocumentVerificationPersonaDocumentVerification(
    targetEntity?: {
      targetEntityTypeId: string;
      targetEntityId: string;
      documentVerificationTypeId: DocumentVerificationTypeId;
    },
    targetEntityQuery?: TargetEntityQuery
  ): Promise<CreateDocumentVerificationPersonaResponse> {
    return this.post<CreateDocumentVerificationPersonaResponse>(
      '/document-verification/persona_document_verification',
      {
        ...(targetEntity ? { targetEntity } : {}),
        ...(targetEntityQuery ? { targetEntityQuery } : {}),
      }
    );
  }
}
